// Billing API fns — TRANSFERRED from `src/api/scribeAPIV2.ts` at D3
// (ARCFE-977), which now re-exports them.
//
// DOMAIN RULING: both were `@domain workspace` and were carried as
// workspace/identity-projection's NAMED exclusions (ARCFE-956) precisely
// because everyone knew they were commerce's. This is the transfer that
// executes that ruling — not a re-tag of unmeasured surface. Identity's
// baseline sheds exactly these two endpoints in the same commit that
// `commerce/billing`'s baseline gains them.
//
// `getTeamBillingDetails` is deliberately NOT here: it belongs to
// `workspace/organization-teams`, a different family, so moving it would be a
// second transfer with a different counterparty and would break this PR's
// one-to-one union proof.

import { getScribeHttpClients } from '@heidi/api/client';
import {
  type AddOnBillingInfoResponse,
  type BillingInfoResponse,
  type BillingPortalLinkParams,
  type BillingPortalSessionResponse,
  type Heidi2UpgradePreviewResponse,
  type RecordTeamUpgradeIntentRequest,
  type RequestHeidi2AccessRequest,
  type RequestHeidi2AccessResponse,
  type TeamUpgradeIntentResponse,
  type TeamUpgradePreviewRequest,
} from '../../contracts';
import { getCommerceDataConfig, type CommerceHttpClient } from '../config';

const http = (): CommerceHttpClient => getScribeHttpClients().scribeHttpV2;
const httpV1 = (): CommerceHttpClient => getScribeHttpClients().scribeHttp;

const ACTIVATE_HEIDI_2_TIMEOUT_MS = 15_000;
const REQUEST_HEIDI_2_ACCESS_TIMEOUT_MS = 15_000;

/** @domain commerce */
export const activateHeidi2 = () =>
  http().post('/billings/activate-heidi-2', undefined, {
    timeout: ACTIVATE_HEIDI_2_TIMEOUT_MS,
  });

/** @domain commerce */
export const requestHeidi2Access = ({ message }: RequestHeidi2AccessRequest = {}) => {
  const trimmed = message?.trim();
  return http().post<RequestHeidi2AccessResponse>(
    '/billings/request-heidi-2-access',
    trimmed ? { message: trimmed } : {},
    { timeout: REQUEST_HEIDI_2_ACCESS_TIMEOUT_MS }
  );
};

const TEAM_UPGRADE_INTENT_PATH = '/billings/team-upgrade-intent';

/** @domain commerce */
export const previewTeamUpgradeIntent = (body: TeamUpgradePreviewRequest) =>
  http().post<Heidi2UpgradePreviewResponse>(`${TEAM_UPGRADE_INTENT_PATH}/preview`, body);

/** @domain commerce */
export const recordTeamUpgradeIntent = (body: RecordTeamUpgradeIntentRequest) =>
  http().post<TeamUpgradeIntentResponse>(TEAM_UPGRADE_INTENT_PATH, body);

/** @domain commerce */
export const getTeamUpgradeIntent = () =>
  http().get<TeamUpgradeIntentResponse>(TEAM_UPGRADE_INTENT_PATH);

/** @domain commerce */
export const getUserBillingInfo = (team_id?: string) => {
  return http().get<BillingInfoResponse>(`/billings`, {
    params: {
      team_id,
    },
  });
};

/** @domain commerce */
export const getUserAddOnBillingInfo = () => {
  return http().get<AddOnBillingInfoResponse>(`/billings/add-on-products`);
};

/** Shared axios instances have no default timeout — a hung portal fetch must
 *  not leave a click-to-redirect waiting forever. */
const STRIPE_BILLING_PORTAL_TIMEOUT_MS = 15_000;

/** @domain commerce */
export const getStripeLink = (opts?: BillingPortalLinkParams) => {
  const { resolveStripeRedirectBase } = getCommerceDataConfig();
  return httpV1().get<BillingPortalSessionResponse>(`/user/billing-portal`, {
    params: {
      ...opts,
      fe_endpoint: `${resolveStripeRedirectBase()}/scribe`,
    },
    timeout: STRIPE_BILLING_PORTAL_TIMEOUT_MS,
  });
};
